import type { AccountingProviderId } from '@/core/models/trial-balance.models';
import { TrialBalanceImportMode } from '@/core/models/trial-balance.models';

/** BUG-038: the accounting packages a trial balance can be pulled from, by the Mode of Import that selects them. */
export const ACCOUNTING_PROVIDERS: Record<number, { id: AccountingProviderId; name: string }> = {
  [TrialBalanceImportMode.QuickBooks]: { id: 'quickbooks', name: 'QuickBooks' },
  [TrialBalanceImportMode.Xero]: { id: 'xero', name: 'Xero' },
  [TrialBalanceImportMode.FreeAgent]: { id: 'freeagent', name: 'FreeAgent' },
};

/** What the /oauth/callback page tells the window that opened the sign-in popup. */
export const OAUTH_MESSAGE_TYPE = 'capium-accounting-oauth';

export interface OAuthMessage {
  type: typeof OAUTH_MESSAGE_TYPE;
  ok: boolean;
  provider: string;
  /** Why it failed (denied, expired, ...); the connected organisation's name on success. */
  message: string | null;
}

export function isOAuthMessage(data: unknown): data is OAuthMessage {
  return typeof data === 'object' && data !== null && (data as { type?: unknown }).type === OAUTH_MESSAGE_TYPE;
}

/** The same-origin channel that also carries the result, for a browser that cuts the popup off from its opener. */
const OAUTH_CHANNEL = 'capium-accounting-oauth';

/**
 * Called by the /oauth/callback page: tells the window that opened the popup, and any other window of this app, how the
 * sign-in ended. Only this app's origin can receive it and it carries no token - only whether it worked and why not.
 */
export function announceOAuthResult(message: Omit<OAuthMessage, 'type'>): void {
  const payload: OAuthMessage = { type: OAUTH_MESSAGE_TYPE, ...message };
  window.opener?.postMessage(payload, window.location.origin);
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(OAUTH_CHANNEL);
    channel.postMessage(payload);
    channel.close();
  }
}

/**
 * Waits for the sign-in popup to report back. Only this app's own origin can answer (the callback page is ours), so
 * another site cannot answer for the user. Resolves with null when the popup is closed first (the user gave up) or
 * `signal` aborts (the dialog went away); the popup's closing is polled because a window gives no event for it.
 */
export function waitForOAuthResult(popup: Window, signal?: AbortSignal): Promise<OAuthMessage | null> {
  return new Promise((resolve) => {
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(OAUTH_CHANNEL) : null;
    const finish = (result: OAuthMessage | null) => {
      window.removeEventListener('message', onMessage);
      channel?.close();
      window.clearInterval(timer);
      signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.origin === window.location.origin && isOAuthMessage(event.data)) {
        finish(event.data);
      }
    };
    const onAbort = () => finish(null);
    const timer = window.setInterval(() => {
      if (popup.closed) {
        finish(null);
      }
    }, 400);
    window.addEventListener('message', onMessage);
    if (channel) {
      channel.onmessage = (event: MessageEvent) => {
        if (isOAuthMessage(event.data)) {
          finish(event.data);
        }
      };
    }
    signal?.addEventListener('abort', onAbort);
    if (signal?.aborted) {
      finish(null);
    }
  });
}

/**
 * A popup that reads as closed may only have been cut off from this window (some sign-in pages do that) while the
 * sign-in went on and finished. Asks the API whether the client is connected now, a few times, before the sign-in is
 * called abandoned. Only for a client that was not connected before: a connection that exists then can only be this one.
 */
export async function connectionAppearsAfterClose(
  isConnected: () => Promise<boolean>,
  signal?: AbortSignal,
  attempts = 8,
  delayMs = 2000,
): Promise<boolean> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (signal?.aborted) {
      return false;
    }
    try {
      if (await isConnected()) {
        return true;
      }
    } catch {
      // not answering: try again
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    }
  }
  return false;
}
